import useMyInfo from '@api/hooks/auth/useMyInfo';
import useProfileStats from '@api/hooks/profile/useProfileStats';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { getOfflineSubscriptionUIDs } from '@api/selectors/subscription';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { nFormatter } from '@utils/textHelper';
import { Divider } from 'aquilla/core/atoms/Divider';
import { Typography } from 'aquilla/core/atoms/Typography';
import React, { useEffect, useRef, useState } from 'react';
import { getMeUsername } from '@api/selectors/auth';
import useProfile from '@api/hooks/profile/useProfile';
import { useIsMobile } from '@stores/AppCommonData';
import Button from 'aquilla/core/molecules/Button';
import useEducatorFollowers from '@api/hooks/educator/useEducatorFollowers';
import EducatorDedicationStats from './EducatorDedicationStatsNew';
import LearnerFollowing from './LearnerFollowing';

const StatsWrapper = styled.div`
  display: flex;
  > button {
    margin-left: ${Spacings.SPACING_10B};
  }
  ${mediaQuery.uptoTablet} {
    justify-content: space-evenly;
    gap: ${Spacings.SPACING_3B};

    > button:last-of-type {
      display: none;
    }
  }
`;
const Stats = styled.div``;
const Label = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_1B};
  text-transform: uppercase;

  ${mediaQuery.uptoTablet} {
    font-size: 10px;
  }
`;
const Value = styled(Typography)`
  font-size: 18px;
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    text-align: center;
    font-size: ${Fonts.LARGE};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0 ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const FollowContainer = styled.div`
  padding: ${Spacings.SPACING_4B};
`;

const FixedFollowButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  padding: ${Spacings.SPACING_4B};
  width: 100%;
  display: none;
  background-color: var(--color-base-1);
  border-top: 1px solid var(--color-i-divider);

  &.show {
    display: block;
  }

  ${mediaQuery.aboveTablet} {
    display: none !important;
  }
`;

const FollowButton = styled(Button)`
  min-width: 176px;
`;

const EducatorStatsNew = (props) => {
  const {
    username,
    isEducator,
    isExEducator,
    onFollowClick,
    isPlatinumEducator
  } = props;
  const [showLearnerFollowing, setShowLearnerFollowing] = useState(false);
  const isMobile = useIsMobile();
  const followButton = useRef(null);
  const selectedGoalUID = useSelectedGoalUID();
  const { profileData: educator } = useProfile(username);
  const { dedicationsCount: totalDedications } = educator;
  const { followedEducators = [] } = useEducatorFollowers();
  const isFollowing = !!followedEducators.find(
    (followedEducator) => followedEducator.username === username
  );
  const offlineSubscription = useMyInfo(getOfflineSubscriptionUIDs);
  const isOfflineSubscription = offlineSubscription.includes(selectedGoalUID);
  const { profileStats: stats = {} } = useProfileStats(username);
  const myUsername = useMyInfo(getMeUsername);
  const totalWatchTime = Math.round(
    stats.statistics?.totalWatchTimeMinutes / 60
  );

  const onCloseLearnerFollowing = () => setShowLearnerFollowing(false);

  const educatorItemsToShow = [
    {
      label: 'Watch mins',
      value: nFormatter(totalWatchTime) || '-'
    },
    {
      label: 'Dedication',
      hide: totalDedications <= 0
    },
    {
      label: 'Followers',
      value: nFormatter(stats.followers) || '-'
    }
  ];
  const exEducatorItemsToShow = [
    {
      label: 'Watch mins',
      value: nFormatter(totalWatchTime) || '-'
    }
  ];

  // eslint-disable-next-line no-nested-ternary
  const items = isEducator
    ? educatorItemsToShow
    : isExEducator
    ? exEducatorItemsToShow
    : [];

  const callback = (entries) => {
    entries.forEach((entry) => {
      const button = document.querySelector('#fixed-follow');
      if (entry.intersectionRatio < 0.1) {
        button.classList.add('show');
      } else {
        button.classList.remove('show');
      }
    });
  };
  useEffect(() => {
    let tempObserver;
    if (isMobile) {
      const options = {
        rootMargin: '-48px 0px 0px 0px',
        threshold: [0, 1]
      };
      tempObserver = new IntersectionObserver(callback, options);
      const target = followButton.current;
      if (target) tempObserver.observe(target);
    }

    return () => {
      if (tempObserver) tempObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const showFollowButton =
    !isPlatinumEducator &&
    !(myUsername === username?.toLowerCase()) &&
    !isExEducator;

  const getStats = () => {
    return items.map((item, index) => {
      let component = null;
      if (
        !isOfflineSubscription &&
        item.label.toLowerCase() === 'dedication' &&
        totalDedications > 0
      )
        component = <EducatorDedicationStats username={username} />;
      else if (
        !isOfflineSubscription &&
        item.label.toLowerCase() !== 'dedication'
      )
        component = (
          <Stats
            key={index.toString()}
            bgColor="var(--color-base-1)"
            onClick={item.onClick}
          >
            <Label>{item.label}</Label>
            <Value>{item.value}</Value>
          </Stats>
        );
      if (component)
        return (
          <>
            {component}
            {items.length !== index + 1 && (
              <StyledDivider orientation="vertical" />
            )}
          </>
        );
      return component;
    });
  };
  return (
    <>
      <StatsWrapper>
        {getStats()}
        <LearnerFollowing
          showLearnerFollowing={showLearnerFollowing}
          learnerUsername={username}
          onCloseLearnerFollowing={onCloseLearnerFollowing}
          onClickFollow={onFollowClick}
        />
        {showFollowButton && (
          <FollowButton
            onClick={onFollowClick}
            variant={isFollowing ? 'secondary' : 'primary'}
          >
            {isFollowing ? 'Following' : 'Follow'}
          </FollowButton>
        )}
      </StatsWrapper>
      {isEducator && !isOfflineSubscription && (
        <>
          <FollowContainer
            bgColor="var(--color-base-1)"
            onClick={items[2].onClick}
          >
            {showFollowButton && (
              <Button
                fullWidth
                ref={followButton}
                onClick={onFollowClick}
                variant={isFollowing ? 'secondary' : 'primary'}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            )}
          </FollowContainer>
        </>
      )}
      {showFollowButton && (
        <FixedFollowButtonContainer id="fixed-follow">
          {showFollowButton && (
            <Button
              fullWidth
              onClick={onFollowClick}
              variant={isFollowing ? 'secondary' : 'primary'}
              size="large"
            >
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
          )}
        </FixedFollowButtonContainer>
      )}
    </>
  );
};

export default EducatorStatsNew;
